import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import CustomPaginator from '../../components/CustomPaginator';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import GlobalDeleteModal from '../../components/GlobalDeleteModal';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import ProfilePictureHover from '../../components/ProfilePictureHover';
import { useSchoolDetailsStore } from '../../stateManagers/hooks/useSchoolDetailsStore';
import useClassesStore from '../../stateManagers/store/useClassesStore';
import StudentFeePanel from './parts/StudentFeePanel';
import FeeHistorySkeletonLoader from '../../skeletons/FeeHistorySkeletonLoader';

// ─── Types matching GetFeePaymentHistory's historyRecordResponse ───────────
interface FeeItem {
  [key: string]: any;
}

interface HistoryRecord {
  student_id: string;
  student_name: string;
  admission_no: string;
  profile_picture_url: string;
  father_name: string;
  father_no: string;
  mother_name: string;
  mother_no: string;
  class_id: string;
  class_name: string;
  section: string;
  roll_no: number;
  fee_record_id: string;
  month_year: string;
  receipt_no: string;
  paid_date: string;
  total_amount: number;
  paid_amount: number;
  fees_status: string;
  pay_mode: string;
  is_exempted: boolean;
  fee_items: FeeItem[];
  optional_services: string[];
  late_fee_applied: number;
}

interface PaginationData {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
}

interface SummaryData {
  total_records: number;
  total_collected: number;
}

interface DeleteTarget {
  ids: string[];
  exitSelectMode: boolean;
}

interface FeeHistoryPanelProps {
  // Optional — pass to scope this panel to a single student (e.g. inside a
  // student profile page). Omit for the standalone "all payments" view.
  studentId?: string;
}

const PAY_MODES = ['All', 'Cash', 'Online', 'Cheque'];

const chipStyle = (active: boolean, activeColor: string): React.CSSProperties => ({
  padding: '6px 16px',
  border: active ? `1.5px solid ${activeColor}` : '1.5px solid #e5e7eb',
  borderRadius: '8px',
  background: active
    ? `linear-gradient(135deg, ${activeColor}dd 0%, ${activeColor} 100%)`
    : 'white',
  color: active ? 'white' : '#1f2937',
  fontSize: '11.5px',
  fontWeight: 600,
  cursor: 'pointer',
  outline: 'none',
  boxShadow: active ? `0 2px 8px ${activeColor}40` : 'none',
  transition: 'all 0.2s ease',
});

const sectionLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '11.5px',
  fontWeight: 700,
  color: '#1e40af',
  marginBottom: '10px',
  letterSpacing: '0.02em',
};

const cbStyle: React.CSSProperties = {
  width: 17,
  height: 17,
  cursor: 'pointer',
  accentColor: '#3b82f6',
};

const CLASS_COLOR = '#3b82f6';
const SECTION_COLOR = '#0d9488';
const PAY_MODE_COLOR = '#4169E1';

const BULK_DELETE_ENDPOINT = '/api/fee-records/bulk-delete';

// ─── Month helpers (same as FeesPanel) ─────────────────────────────────────
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const monthNumberToName = (month?: number | null): string | null => {
  if (month == null || month < 1 || month > 12) return null;
  return MONTH_NAMES[month - 1];
};

const FeeHistoryPanel: React.FC<FeeHistoryPanelProps> = ({ studentId }) => {
  const toast = useRef<Toast>(null);

  const schoolDetails = useSchoolDetailsStore((s) => s.schoolDetails);
  const currentSession = schoolDetails?.current_session ?? null;
  const SESSION_ID = currentSession?.id ?? null;
  const SESSION = currentSession?.session ?? null;
  const SESSION_START = monthNumberToName(currentSession?.start_month);
  const SESSION_END = monthNumberToName(currentSession?.end_month);

  const { classOptions, fetchClasses } = useClassesStore();

  const [records, setRecords] = useState<HistoryRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedPayMode, setSelectedPayMode] = useState('All');

  const [paginationData, setPaginationData] = useState<PaginationData>({
    current_page: 1,
    per_page: 20,
    total_items: 0,
    total_pages: 1,
  });
  const [summary, setSummary] = useState<SummaryData>({
    total_records: 0,
    total_collected: 0,
  });

  // Side panel (same pattern as FeesPanel)
  const [panelStudentId, setPanelStudentId] = useState<string | null>(null);
  const [panelStudentRecord, setPanelStudentRecord] = useState<HistoryRecord | null>(null);
  const panelOpen = panelStudentId !== null;

  // ─── Multi-select + delete state (same pattern as StudentsListDashboard) ──
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  // Non-null => GlobalDeleteModal is open for these ids
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  const selectedClassData = classOptions.find((c) => c.id === selectedClassId);
  const sections = selectedClassData?.sections || [];
  const hasSections = sections.length > 0;

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const fetchHistory = useCallback(
    async (currentPage: number) => {
      if (!SESSION_ID) return;

      // Selection is page-scoped — any refetch (page/filter/session/delete) resets it.
      setSelectedIds(new Set());

      setLoading(true);
      try {
        const params: Record<string, string | number> = {
          session_id: SESSION_ID,
          page: currentPage,
          limit,
        };
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        if (selectedClassId) params.class_id = selectedClassId;
        if (selectedSection) params.section = selectedSection;
        if (studentId) params.student_id = studentId;
        if (selectedPayMode !== 'All') params.pay_mode = selectedPayMode.toLowerCase();

        const response = await apiClient.get('/api/fee-records/history', { params });

        if (response.data.status === 'success') {
          setRecords(response.data.data || []);
          setPaginationData(response.data.pagination);
          setSummary(response.data.summary);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: response.data.message || 'Failed to fetch payment history',
            life: 5000,
          });
        }
      } catch (err: any) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: err.response?.data?.message || 'Failed to fetch payment history',
          life: 5000,
        });
      } finally {
        setLoading(false);
      }
    },
    [SESSION_ID, limit, startDate, endDate, selectedClassId, selectedSection, selectedPayMode, studentId],
  );

  // Always points at the latest fetchHistory — lets setTimeout callbacks
  // (handleClearFilters) see fresh filter state instead of a stale closure.
  const fetchHistoryRef = useRef(fetchHistory);
  useEffect(() => {
    fetchHistoryRef.current = fetchHistory;
  }, [fetchHistory]);

  useEffect(() => {
    fetchHistory(1);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SESSION_ID]);

  const handleApplyFilters = () => {
    setPage(1);
    fetchHistory(1);
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedClassId('');
    setSelectedSection('');
    setSelectedPayMode('All');
    setPage(1);
    setTimeout(() => fetchHistoryRef.current(1), 0);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= paginationData.total_pages && newPage !== page) {
      setPage(newPage);
      fetchHistory(newPage);
    }
  };

  const handleViewStudent = (record: HistoryRecord) => {
    if (panelStudentId === record.student_id) {
      setPanelStudentId(null);
      setPanelStudentRecord(null);
    } else {
      setPanelStudentId(record.student_id);
      setPanelStudentRecord(record);
    }
  };

  // ─── Selection handlers ──────────────────────────────────────────────────
  const selectableRecords = records.filter((r) => !!r.fee_record_id);
  const allOnPageChecked =
    selectableRecords.length > 0 && selectableRecords.every((r) => selectedIds.has(r.fee_record_id));
  const someOnPageChecked = selectableRecords.some((r) => selectedIds.has(r.fee_record_id));

  const toggleSelectionMode = () => {
    setSelectionMode((prev) => !prev);
    clearSelection();
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      selectableRecords.forEach((r) => (checked ? next.add(r.fee_record_id) : next.delete(r.fee_record_id)));
      return next;
    });
  };

  // ─── Delete (single + bulk share the same endpoint) ──────────────────────
  const deleteRecords = async (ids: string[], exitSelectMode = false) => {
    if (ids.length === 0) return;
    setDeleting(true);
    try {
      const response = await apiClient.post(BULK_DELETE_ENDPOINT, { ids });
      const data = response.data;

      if (data.status === 'success') {
        const deletedCount = data.deleted_count ?? ids.length;
        toast.current?.show({
          severity: 'success',
          summary: 'Deleted',
          detail: `${deletedCount} payment record${deletedCount !== 1 ? 's' : ''} deleted successfully!`,
          life: 4000,
        });

        // If we just emptied the current page, step back one page.
        const remainingOnPage = records.length - ids.length;
        const nextPage = remainingOnPage <= 0 && page > 1 ? page - 1 : page;

        // Close side panel if it was open (its data may now be stale).
        setPanelStudentId(null);
        setPanelStudentRecord(null);

        if (exitSelectMode) setSelectionMode(false);
        setPage(nextPage);
        await fetchHistory(nextPage);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: data.message || 'Failed to delete payment records!',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'An error occurred while deleting',
        life: 5000,
      });
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleDeleteOne = (record: HistoryRecord) => {
    if (!record.fee_record_id) return;
    setDeleteTarget({ ids: [record.fee_record_id], exitSelectMode: false });
  };

  const handleBulkDelete = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setDeleteTarget({ ids, exitSelectMode: true });
  };

  const handleDeleteModalClose = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  const handleDeleteModalConfirm = () => {
    if (!deleteTarget) return;
    deleteRecords(deleteTarget.ids, deleteTarget.exitSelectMode);
  };

  // Records shown inside the modal (resolved from current page)
  const deleteTargetRecords = deleteTarget
    ? records.filter((r) => deleteTarget.ids.includes(r.fee_record_id))
    : [];
  const isBulkDelete = (deleteTarget?.ids.length ?? 0) > 1;
  const PREVIEW_LIMIT = 5;

  const hasOptionalFilters =
    !!startDate || !!endDate || !!selectedClassId || !!selectedSection || selectedPayMode !== 'All';

  const panelShift: React.CSSProperties = {
    marginRight: panelOpen ? 436 : 0,
    transition: 'margin-right 0.28s cubic-bezier(0.4,0,0.2,1)',
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <style>{`
        @keyframes bulkBarIn { from { opacity:0; transform:translateY(-6px) } to { opacity:1; transform:translateY(0) } }
        .fee-history-container { background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); min-height: 100vh; }
        .fee-history-content { background: white; border-radius: 20px; box-shadow: 0 2px 12px rgba(0,0,0,0.06); border: 1px solid #e5e7eb; overflow: hidden; }
        .fee-history-table-container { overflow-x: auto; }
        .fee-history-table { width: 100%; border-collapse: collapse; }
        .fee-history-table thead { background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%); border-bottom: 2px solid #e5e7eb; }
        .fee-history-table th { padding: 16px 20px; text-align: left; font-size: 11px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.8px; }
        .fee-history-table tbody tr { border-bottom: 1px solid #f3f4f6; transition: all 0.2s; }
        .fee-history-table tbody tr:hover { background: linear-gradient(to right, rgba(65,105,225,0.02) 0%, rgba(65,105,225,0.01) 100%); }
        .fee-history-table tbody tr.row--active { background: rgba(65,105,225,0.05) !important; }
        .fee-history-table tbody tr.row--selected { background: rgba(59,130,246,0.1) !important; box-shadow: inset 4px 0 0 0 #3b82f6; }
        .fee-history-table tbody tr:last-child { border-bottom: none; }
        .fee-history-table td { padding: 16px 20px; font-size: 13px; color: #1f2937; font-weight: 500; }
        .student-info { display: flex; align-items: center; gap: 12px; }
        .student-details { display: flex; flex-direction: column; gap: 2px; }
        .student-name { font-weight: 600; color: #1f2937; font-size: 13px; }
        .student-meta { font-size: 11px; color: #9ca3af; font-weight: 500; }
        .info-with-icon { display: flex; align-items: center; gap: 8px; }
        .status-badge { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 20px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .status-badge.paid { background: linear-gradient(135deg, rgba(16,185,129,0.1), rgba(5,150,105,0.08)); color: #059669; border: 1px solid rgba(16,185,129,0.2); }
        .status-badge.partial, .status-badge.partially_paid { background: linear-gradient(135deg, rgba(245,158,11,0.1), rgba(217,119,7,0.08)); color: #d97706; border: 1px solid rgba(245,158,11,0.2); }
        .status-badge.exempted { background: linear-gradient(135deg, rgba(99,102,241,0.1), rgba(79,70,229,0.08)); color: #4f46e5; border: 1px solid rgba(99,102,241,0.2); }
        .empty-state { padding: 80px 20px; text-align: center; }
        .empty-state-icon { width: 80px; height: 80px; margin: 0 auto 20px; border-radius: 50%; background: linear-gradient(135deg, rgba(65,105,225,0.1), rgba(91,124,230,0.05)); display: flex; align-items: center; justify-content: center; }
        .empty-state h3 { margin: 0 0 8px; font-size: 18px; font-weight: 700; color: #1f2937; }
        .empty-state p { margin: 0; font-size: 14px; color: #9ca3af; font-weight: 500; }
        .p-button-text:hover { background: #f3f4f6 !important; }
        .fh-date-input { border: 1.5px solid #e5e7eb; border-radius: 8px; padding: 6px 10px; font-size: 12px; font-weight: 600; color: #1f2937; outline: none; }
        .fh-date-input:focus { border-color: #4169E1; }
        .chip-button { transition: all 0.2s ease; }
        .chip-button:hover { transform: translateY(-1px); }
        .chip-button:active { transform: translateY(0); }
        .bulk-action-bar { animation:bulkBarIn .22s ease; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; background:linear-gradient(135deg,#1e40af,#3b82f6); border-radius:14px; padding:12px 18px; margin-bottom:16px; box-shadow:0 4px 16px rgba(59,130,246,.3); }
        .bulk-count { font-size:13px; font-weight:700; color:white; }
        .bulk-count span { opacity:.75; font-weight:500; }
        .bulk-action-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border-radius:10px; font-size:12px; font-weight:600; cursor:pointer; border:none; transition:all .15s; }
        .bulk-action-btn:disabled { opacity:.5; cursor:not-allowed; }
        .bulk-action-btn.danger { background:linear-gradient(135deg,#ef4444,#dc2626); color:white; box-shadow:0 2px 8px rgba(239,68,68,.35); }
        .bulk-action-btn.danger:hover:not(:disabled) { background:linear-gradient(135deg,#f87171,#ef4444); }
        .bulk-action-btn.ghost { background:rgba(255,255,255,.15); color:white; border:1px solid rgba(255,255,255,.3); }
        .bulk-action-btn.ghost:hover:not(:disabled) { background:rgba(255,255,255,.25); }
        .select-mode-btn { display:inline-flex; align-items:center; gap:6px; padding:6px 14px; border-radius:10px; font-size:12px; font-weight:600; cursor:pointer; transition:all .2s; border:1.5px solid; }
        .select-mode-btn.off { background:white; color:#4169E1; border-color:#c7d2fe; }
        .select-mode-btn.off:hover { background:#eff6ff; }
        .select-mode-btn.on { background:#4169E1; color:white; border-color:#4169E1; }
      `}</style>

      <div className="fee-history-container">
        <GlobalHeaderTitle
          title="Fee Payment History"
          subtitle={`All recorded payments${studentId ? ' for this student' : ''}`}
          icon="pi pi-history"
          gradientFrom="#4169E1"
          gradientTo="#5B7CE6"
        />

        {/* Filter bar */}
        <div
          style={{
            background: 'white',
            borderRadius: 16,
            padding: '16px 20px',
            marginBottom: 20,
            boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
            border: '1px solid rgba(59, 130, 246, 0.12)',
            ...panelShift,
          }}
        >
          {/* Class (+ Section) / Date Range + Payment Mode — side by side columns */}
          <div style={{ display: 'flex', gap: 20, flexWrap: 'nowrap' }}>
            {/* Class (+ Section stacked below it) — hidden entirely when scoped to a single student */}
            {!studentId && (
              <div style={{ flex: '0 1 calc(55% - 10px)', minWidth: 0 }}>
                <label style={sectionLabelStyle}>Class</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {classOptions.map((cls) => (
                    <button
                      key={cls.id}
                      className="chip-button"
                      onClick={() =>
                        setSelectedClassId((prev) => {
                          const next = prev === cls.id ? '' : cls.id;
                          if (next === '') setSelectedSection('');
                          return next;
                        })
                      }
                      style={chipStyle(selectedClassId === cls.id, CLASS_COLOR)}
                    >
                      {cls.label}
                    </button>
                  ))}
                </div>
                {hasSections && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
                    <label style={sectionLabelStyle}>Section</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {sections.map((section) => (
                        <button
                          key={section}
                          className="chip-button"
                          onClick={() =>
                            setSelectedSection((prev) => (prev === section ? '' : section))
                          }
                          style={chipStyle(selectedSection === section, SECTION_COLOR)}
                        >
                          Section {section}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Date Range (+ Payment Mode stacked below it) */}
            <div
              style={{
                flex: studentId ? '1 1 100%' : '0 1 calc(45% - 10px)',
                minWidth: 0,
                paddingLeft: studentId ? 0 : 20,
                borderLeft: studentId ? 'none' : '1px solid #f3f4f6',
              }}
            >
              <label style={sectionLabelStyle}>Date Range</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="date"
                  className="fh-date-input"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>to</span>
                <input
                  type="date"
                  className="fh-date-input"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
                <label style={sectionLabelStyle}>Payment Mode</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {PAY_MODES.map((mode) => (
                    <button
                      key={mode}
                      className="chip-button"
                      onClick={() => setSelectedPayMode(mode)}
                      style={chipStyle(selectedPayMode === mode, PAY_MODE_COLOR)}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              marginTop: 18,
              paddingTop: 16,
              borderTop: '1px solid #f3f4f6',
            }}
          >
            {hasOptionalFilters && (
              <button
                onClick={handleClearFilters}
                className="chip-button"
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: '1.5px solid #e5e7eb',
                  background: 'white',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#6b7280',
                  cursor: 'pointer',
                }}
              >
                Clear All
              </button>
            )}
            <button
              onClick={handleApplyFilters}
              className="chip-button"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 18px',
                borderRadius: 8,
                border: 'none',
                background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)',
                fontSize: 12,
                fontWeight: 700,
                color: 'white',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(65, 105, 225, 0.3)',
              }}
            >
              <i className="pi pi-check" style={{ fontSize: 12 }} />
              Apply Filters
            </button>
          </div>
        </div>

        {/* Summary chips + Select toggle */}
        <div
          style={{
            display: 'flex',
            gap: 10,
            marginBottom: 16,
            flexWrap: 'wrap',
            alignItems: 'center',
            ...panelShift,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 14px',
              borderRadius: 10,
              background: 'rgba(59,130,246,0.08)',
              border: '1px solid rgba(59,130,246,0.2)',
            }}
          >
            <i className="pi pi-file" style={{ fontSize: 14, color: '#2563eb' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>
              {summary.total_records}
            </span>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
              Records
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 14px',
              borderRadius: 10,
              background: 'rgba(16,185,129,0.08)',
              border: '1px solid rgba(16,185,129,0.2)',
            }}
          >
            <i className="pi pi-indian-rupee" style={{ fontSize: 14, color: '#059669' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#047857' }}>
              {summary.total_collected.toLocaleString('en-IN')}
            </span>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
              Collected
            </span>
          </div>

          {records.length > 0 && !loading && (
            <button
              className={`select-mode-btn ${selectionMode ? 'on' : 'off'}`}
              onClick={toggleSelectionMode}
              disabled={deleting}
              style={{ marginLeft: 'auto' }}
            >
              <i className="pi pi-check-square" style={{ fontSize: 12 }} />
              {selectionMode ? 'Exit Select' : 'Select'}
            </button>
          )}
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="bulk-action-bar" style={panelShift}>
            <div className="bulk-count">
              {selectedIds.size} record{selectedIds.size !== 1 ? 's' : ''} selected{' '}
              <span>of {records.length} on this page</span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="bulk-action-btn danger" onClick={handleBulkDelete} disabled={deleting}>
                <i className={`pi ${deleting ? 'pi-spin pi-spinner' : 'pi-trash'}`} style={{ fontSize: 12 }} />{' '}
                {deleting ? 'Deleting...' : 'Delete Selected'}
              </button>
              <button className="bulk-action-btn ghost" onClick={clearSelection} disabled={deleting}>
                <i className="pi pi-times" style={{ fontSize: 12 }} /> Clear Selection
              </button>
            </div>
          </div>
        )}

        {/* Table / Skeleton */}
        <div style={panelShift}>
          {loading ? (
            <FeeHistorySkeletonLoader />
          ) : records.length === 0 ? (
            <div className="fee-history-content">
              <div className="empty-state">
                <div className="empty-state-icon">
                  <i className="pi pi-inbox" style={{ fontSize: 32, color: '#4169E1' }} />
                </div>
                <h3>No Payment History Found</h3>
                <p>There are no payments matching the selected filters.</p>
              </div>
            </div>
          ) : (
            <div className="fee-history-content">
              <div className="fee-history-table-container">
                <table className="fee-history-table">
                  <thead>
                    <tr>
                      {selectionMode && (
                        <th style={{ width: 52, textAlign: 'center' }}>
                          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <input
                              type="checkbox"
                              checked={allOnPageChecked}
                              ref={(el) => {
                                if (el) el.indeterminate = someOnPageChecked && !allOnPageChecked;
                              }}
                              onChange={(e) => toggleAll(e.target.checked)}
                              disabled={deleting || selectableRecords.length === 0}
                              style={cbStyle}
                            />
                          </div>
                        </th>
                      )}
                      <th>Student</th>
                      <th>Class & Section</th>
                      <th>Month</th>
                      <th>Receipt No</th>
                      <th>Paid Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Pay Mode</th>
                      <th style={{ textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((record) => {
                      const statusLower = (record.fees_status || '').toLowerCase();
                      const isPartial = statusLower === 'partial' || statusLower === 'partially_paid';
                      const isSelected = !!record.fee_record_id && selectedIds.has(record.fee_record_id);
                      const isPanelActive = panelStudentId === record.student_id;

                      const rowClass = isSelected ? 'row--selected' : isPanelActive ? 'row--active' : '';

                      return (
                        <tr
                          key={record.fee_record_id || `${record.student_id}-${record.month_year}`}
                          className={rowClass}
                        >
                          {selectionMode && (
                            <td style={{ textAlign: 'center' }}>
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleOne(record.fee_record_id)}
                                  disabled={deleting || !record.fee_record_id}
                                  style={cbStyle}
                                />
                              </div>
                            </td>
                          )}
                          <td>
                            <div className="student-info">
                              <ProfilePictureHover
                                src={record.profile_picture_url}
                                name={record.student_name}
                                size={38}
                                previewSize={240}
                                borderRadius={10}
                              />
                              <div className="student-details">
                                <span className="student-name">{record.student_name}</span>
                                <span className="student-meta">
                                  Adm: {record.admission_no || '—'} · Roll: {record.roll_no}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div style={{ fontWeight: 600 }}>{record.class_name}</div>
                            {record.section && (
                              <div style={{ fontSize: 11, color: '#9ca3af' }}>
                                Section {record.section}
                              </div>
                            )}
                          </td>
                          <td>{record.month_year}</td>
                          <td>
                            <div className="info-with-icon">
                              <i className="pi pi-file" style={{ fontSize: 14, color: '#9ca3af' }} />
                              {record.receipt_no || '—'}
                            </div>
                          </td>
                          <td>
                            {record.paid_date ? (
                              <div className="info-with-icon">
                                <i className="pi pi-calendar" style={{ fontSize: 14, color: '#9ca3af' }} />
                                {new Date(record.paid_date).toLocaleDateString('en-IN', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                })}
                              </div>
                            ) : (
                              <span style={{ color: '#9ca3af' }}>—</span>
                            )}
                          </td>
                          <td>
                            <div className="info-with-icon" style={{ fontWeight: 600 }}>
                              <i className="pi pi-indian-rupee" style={{ fontSize: 12, color: '#059669' }} />
                              {Number(record.paid_amount ?? 0).toLocaleString('en-IN')}
                              {isPartial && (
                                <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>
                                  / {Number(record.total_amount ?? 0).toLocaleString('en-IN')}
                                </span>
                              )}
                            </div>
                            {record.late_fee_applied > 0 && (
                              <div style={{ fontSize: 11, color: '#d97706', fontWeight: 500 }}>
                                +{record.late_fee_applied.toLocaleString('en-IN')} late fee
                              </div>
                            )}
                          </td>
                          <td>
                            <span className={`status-badge ${record.is_exempted ? 'exempted' : statusLower}`}>
                              <i
                                className={`pi ${
                                  record.is_exempted
                                    ? 'pi-shield'
                                    : statusLower === 'paid'
                                      ? 'pi-check-circle'
                                      : 'pi-clock'
                                }`}
                                style={{ fontSize: 10 }}
                              />
                              {record.is_exempted ? 'Exempted' : record.fees_status}
                            </span>
                          </td>
                          <td>
                            <div className="info-with-icon">
                              <i className="pi pi-credit-card" style={{ fontSize: 14, color: '#9ca3af' }} />
                              {record.pay_mode?.toUpperCase() || 'N/A'}
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                              {!studentId && (
                                <Button
                                  icon="pi pi-eye"
                                  className="p-button-text p-button-rounded"
                                  tooltip={isPanelActive ? 'Close Panel' : 'View Full Details'}
                                  tooltipOptions={{ position: 'top' }}
                                  style={{
                                    color: isPanelActive ? '#4169E1' : '#60a5fa',
                                    width: 32,
                                    height: 32,
                                    background: isPanelActive ? 'rgba(65,105,225,0.1)' : 'transparent',
                                    borderRadius: 8,
                                  }}
                                  onClick={() => handleViewStudent(record)}
                                />
                              )}
                              <Button
                                icon="pi pi-trash"
                                className="p-button-text p-button-rounded"
                                tooltip="Delete"
                                tooltipOptions={{ position: 'top' }}
                                style={{ color: '#ef4444', width: 32, height: 32 }}
                                disabled={deleting || !record.fee_record_id}
                                onClick={() => handleDeleteOne(record)}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {paginationData.total_pages > 1 && (
                <CustomPaginator
                  page={paginationData.current_page}
                  totalPages={paginationData.total_pages}
                  totalRecords={paginationData.total_items}
                  pageSize={paginationData.per_page}
                  onPageChange={handlePageChange}
                  showInfoBar={true}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <GlobalDeleteModal
        open={deleteTarget !== null}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteModalConfirm}
        loading={deleting}
        title={isBulkDelete ? 'Delete Payment Records' : 'Delete Payment Record'}
        subtitle={
          isBulkDelete
            ? `You are about to delete ${deleteTarget?.ids.length} payment records. This action cannot be undone.`
            : 'Are you sure you want to delete this payment record? This action cannot be undone.'
        }
        confirmLabel={isBulkDelete ? `Delete ${deleteTarget?.ids.length} Records` : 'Delete'}
      >
        {deleteTargetRecords.length > 0 && (
          <div
            style={{
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: '10px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              maxHeight: 220,
              overflowY: 'auto',
            }}
          >
            {deleteTargetRecords.slice(0, PREVIEW_LIMIT).map((r) => (
              <div
                key={r.fee_record_id}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#1f2937',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {r.student_name}
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>
                    {r.month_year}
                    {r.receipt_no ? ` · Receipt ${r.receipt_no}` : ''}
                  </div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#059669', flexShrink: 0 }}>
                  ₹{Number(r.paid_amount ?? 0).toLocaleString('en-IN')}
                </div>
              </div>
            ))}
            {deleteTargetRecords.length > PREVIEW_LIMIT && (
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textAlign: 'center' }}>
                +{deleteTargetRecords.length - PREVIEW_LIMIT} more
              </div>
            )}
          </div>
        )}
      </GlobalDeleteModal>

      {/* Student fee detail panel — same pattern as FeesPanel */}
      {panelOpen && (
        <StudentFeePanel
          studentId={panelStudentId}
          session={SESSION ?? ''}
          sessionId={SESSION_ID ?? ''}
          sessionStart={SESSION_START ?? ''}
          sessionEnd={SESSION_END ?? ''}
          classId={panelStudentRecord?.class_id ?? null}
          studentRecord={
            panelStudentRecord
              ? {
                  student_name: panelStudentRecord.student_name,
                  profile_picture_url: panelStudentRecord.profile_picture_url,
                  class: panelStudentRecord.class_name,
                  section: panelStudentRecord.section,
                  roll_no: panelStudentRecord.roll_no,
                }
              : null
          }
          onClose={() => {
            setPanelStudentId(null);
            setPanelStudentRecord(null);
          }}
          onPaymentSuccess={() => fetchHistory(page)}
        />
      )}
    </>
  );
};

export default FeeHistoryPanel;